import {
  ApiSignupVerifyResponseSchema,
  type TApiSignupVerifyResponse,
  type TApiSignupVerifyParams,
  TApiSignupVerifyResponseCookie,
  ApiSignupVerifyResponseCookieSchema,
  TApiSignupCreateParams,
  TApiSignupCreateResponse,
  ApiSignupCreateResponseSchema,
} from "@/api/launcherApp/signup/schema"
import { buildCookieString, buildJSessionIDCookie, parseSetCookieHeader } from "@/api/launcherApp/signup/utils"
import { loggedBackendFetch } from "@/lib/backend-call-logging"
import { createScopedLogger } from "@/lib/observability"
import { makeURL } from "@nauverse/make-url"

const ENDPOINT_BASE_URL = "/launcher-app/signup"
export const SIGNUP_CREATE_TIMEOUT_MS = 5 * 60 * 1_000

type TVerifyProps = TApiSignupVerifyParams

export async function verify(params: TVerifyProps): Promise<TApiSignupVerifyResponse> {
  const logger = createScopedLogger("signup", {
    operation: "verify",
  })
  const baseURL = process.env.NEXT_PUBLIC_BASE_SIGNUP_URL

  if (!baseURL) {
    throw new Error("NEXT_PUBLIC_BASE_SIGNUP_URL is not set")
  }

  const queryURL = makeURL(baseURL, ENDPOINT_BASE_URL, "verify")
  const startedAt = Date.now()

  try {
    const receivedJson = await loggedBackendFetch(
      queryURL,
      {
        method: "POST",
        body: JSON.stringify(params),
        headers: {
          "Content-Type": "application/json",
        },
        cache: "no-store",
        credentials: "include",
      },
      {
        logger,
        event: "signup.verify_backend_request",
      }
    )

    // Get also the cookie
    const cookies = receivedJson.headers.get("Set-Cookie")

    // Parse the cookies with all the cookie parameters
    const parsedCookies = parseSetCookieHeader(cookies ?? "")
    // consola.info("Cookies", parsedCookies)

    const json = (await receivedJson.json()) as Record<string, unknown>
    const parsedJson = ApiSignupVerifyResponseSchema.parse({
      ...json,
      cookies: [...parsedCookies, buildJSessionIDCookie()],
    })
    logger.info("signup.verify.succeeded", {
      status: receivedJson.status,
      setCookieCount: parsedCookies.length,
      durationMs: Date.now() - startedAt,
    })

    return parsedJson
  } catch (err) {
    await logger.error("signup.verify.failed", err, {
      urlHost: new URL(queryURL).host,
      durationMs: Date.now() - startedAt,
    })

    throw new Error("Error verifying signup")
  }
}

export async function create(params: TApiSignupCreateParams): Promise<TApiSignupCreateResponse> {
  const logger = createScopedLogger("signup", {
    operation: "create",
  })
  const baseURL = process.env.NEXT_PUBLIC_BASE_SIGNUP_URL

  if (!baseURL) {
    throw new Error("NEXT_PUBLIC_BASE_SIGNUP_URL is not set")
  }

  const queryURL = makeURL(baseURL, ENDPOINT_BASE_URL, "create")
  const startedAt = Date.now()

  try {
    const cookiesString = buildCookieString(params)
    const receivedJson = await loggedBackendFetch(
      queryURL,
      {
        method: "POST",
        body: JSON.stringify({}),
        headers: {
          "Content-Type": "application/json",
          // Send the cookies by creating a new cookie header
          // Cookie: cookiesString,
        },
        credentials: "include",
        signal: AbortSignal.timeout(SIGNUP_CREATE_TIMEOUT_MS),
        //   credentials: "include",
        cache: "no-store",
      },
      {
        logger,
        event: "signup.create_backend_request",
      }
    )

    const json = (await receivedJson.json()) as Record<string, unknown>
    const parsedJson = ApiSignupCreateResponseSchema.parse(json)
    logger.info("signup.create.succeeded", {
      status: receivedJson.status,
      hasSignupCookiePayload: cookiesString.length > 0,
      durationMs: Date.now() - startedAt,
    })

    return parsedJson
  } catch (err) {
    await logger.error("signup.create.failed", err, {
      urlHost: new URL(queryURL).host,
      durationMs: Date.now() - startedAt,
    })

    throw new Error("Error creating signup")
  }
}
